// src/QRclient.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './QRmessaging/QRclient.css';

// QR 모듈 컴포넌트 가져오기
import QRCreator from './QRmessaging/QRCreator';
import QRViewer from './QRmessaging/QRViewer';
import QRTemplateManager from './QRmessaging/QRTemplateManager';
import QRMessageList from './QRmessaging/QRMessageList';
import QRScanner from './QRmessaging/QRScanner';
import DirectQRCreator from './QRmessaging/DirectQRCreator';

// QR 코드 관련 라이브러리
// import QRCode from 'qrcode.react';
import { QRCodeSVG } from 'qrcode.react';

function QRclient({ language, user, view, templates }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState('create'); // 'create', 'list', 'templates', 'view'
  const [messageData, setMessageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 서버 루트 URL 설정
  const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';
  
  // URL 쿼리 파라미터 확인
  useEffect(() => {
    // QR 코드 뷰어 모드
    if (view || location.pathname.includes('/qr/view')) {
      setActiveView('view');
      const params = new URLSearchParams(location.search);
      const messageId = params.get('id');
      if (messageId) {
        fetchMessage(messageId);
      }
    } 
    // 템플릿 관리 모드
    else if (templates || location.pathname.includes('/qr/templates')) {
      setActiveView('templates');
    }
    // URL에 다른 정보가 없으면 기본값 설정
    else {
      setActiveView('create');
    }
  }, [view, templates, location]);
  
  // 메시지 데이터 가져오기
  const fetchMessage = async (messageId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${SERVER_ROOT}/api/qr/message/${messageId}`, {
        params: {
          email: user?.email
        }
      });
      
      setMessageData(response.data.message);
    } catch (error) {
      console.error('메시지 조회 오류:', error);
      setError(error.response?.data?.error || '메시지를 불러오는 중 오류가 발생했습니다.');
      
      // 비밀번호 보호 메시지 처리
      if (error.response?.data?.requiresPassword) {
        setMessageData({ 
          id: messageId, 
          requiresPassword: true 
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  // 비밀번호 제출 처리
  const handlePasswordSubmit = async (messageId, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${SERVER_ROOT}/api/qr/message/${messageId}`, {
        params: {
          email: user?.email,
          password
        }
      });
      
      setMessageData(response.data.message);
    } catch (error) {
      console.error('비밀번호 검증 오류:', error);
      setError(error.response?.data?.error || '비밀번호가 일치하지 않습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  // QR 코드 생성 성공 처리
  const handleQRCreated = (result) => {
    setMessageData(result);
    setActiveView('success');
  };
  
  // 메인 화면으로 이동
  const goToHome = () => {
    navigate('/qr');
  };
  
  // 탭 전환 처리
  const handleTabChange = (tab) => {
    setActiveView(tab);
  };
  
  // 로딩 화면
  if (loading) {
    return (
      <div className="qr-client-container">
        <div className="qr-loading">
          <div className="qr-spinner"></div>
          <p>{language === 'KR' ? '로딩 중...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }
  
  // 오류 화면
  if (error && activeView === 'view') {
    return (
      <div className="qr-client-container">
        <div className="qr-error">
          <h2>{language === 'KR' ? '오류 발생' : 'Error'}</h2>
          <p>{error}</p>
          <button onClick={goToHome} className="qr-btn">
            {language === 'KR' ? '홈으로 돌아가기' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="qr-client-container">
      <header className="qr-header">
        <h1>{language === 'KR' ? 'QR 코드 메시징' : 'QR Code Messaging'}</h1>
        {!view && (
          <div className="qr-tabs">
            <button 
              className={`qr-tab ${activeView === 'create' ? 'active' : ''}`}
              onClick={() => handleTabChange('create')}
            >
                {language === 'KR' ? '메시지 생성' : 'Create Message'}
            </button>
            <button 
              className={`qr-tab ${activeView === 'list' ? 'active' : ''}`}
              onClick={() => handleTabChange('list')}
            >
              {language === 'KR' ? '메시지 목록' : 'My Messages'}
            </button>
            <button 
              className={`qr-tab ${activeView === 'templates' ? 'active' : ''}`}
              onClick={() => handleTabChange('templates')}
            >
              {language === 'KR' ? '템플릿 관리' : 'Templates'}
            </button>

            {/* <button 
              className={`qr-tab ${activeView === 'scan' ? 'active' : ''}`}
              onClick={() => handleTabChange('scan')}
            >
              {language === 'KR' ? 'QR 코드 읽기' : 'Scan QR Code'}
            </button> */}
            {/* <button 
              className={`qr-tab ${activeView === 'directqr' ? 'active' : ''}`}
              onClick={() => handleTabChange('directqr')}
            >
              {language === 'KR' ? 'Direct QR 코드 만들기' : 'Create Direct QR Code'}
            </button> */}
          </div>
        )}
      </header>
      
      <main className="qr-main">
        {/* QR 코드 생성 화면 */}
        {activeView === 'create' && (
          <QRCreator 
            language={language} 
            user={user} 
            onSuccess={handleQRCreated}
            SERVER_ROOT={SERVER_ROOT}
          />
        )}
        
        {/* Direct QR 코드 생성 화면 */}
        {activeView === 'directqr' && (
          <DirectQRCreator
            language={language}
          />
        )}

        {/* QR 코드 스캔 화면 */}
        {activeView === 'scan' && (
          <QRScanner
            language={language}
          />
        )}

        {/* 생성 성공 화면 */} 
        {activeView === 'success' && messageData && (
        <div className="qr-success">
            <h2>{language === 'KR' ? 'QR 코드가 생성되었습니다!' : 'QR Code Created Successfully!'}</h2>
            
            <div className="qr-result">
            <div className="qr-code-container" id="qrCodeContainer">
                <QRCodeSVG 
                value={messageData.url} 
                size={200}
                level="H"
                includeMargin={true}
                />
            </div>
            
            <div className="qr-actions">
                <button 
                onClick={() => {
                    try {
                    // SVG 요소 선택 및 데이터 추출
                    const svgElement = document.querySelector('#qrCodeContainer svg');
                    const svgData = new XMLSerializer().serializeToString(svgElement);
                    
                    // SVG 데이터를 Base64로 인코딩
                    const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
                    
                    // 이미지 생성 및 로드
                    const img = new Image();
                    img.onload = function() {
                        // Canvas 생성 및 이미지 그리기
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        
                        // Canvas에서 PNG 데이터 URL 생성
                        const pngUrl = canvas.toDataURL('image/png');
                        
                        // 다운로드 링크 생성 및 클릭
                        const downloadLink = document.createElement('a');
                        downloadLink.href = pngUrl;
                        downloadLink.download = 'qrcode.png';
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                    };
                    img.src = 'data:image/svg+xml;base64,' + svgBase64;
                    } catch (error) {
                    console.error('QR 코드 저장 오류:', error);
                    alert(language === 'KR' 
                        ? 'QR 코드 저장 중 오류가 발생했습니다.' 
                        : 'Error saving QR code.');
                    }
                }}
                className="qr-btn"
                >
                {language === 'KR' ? 'QR 코드 저장' : 'Save QR Code'}
                </button>
                
                <button 
                onClick={() => {
                    try {
                    // SVG 요소 선택 및 데이터 추출
                    const svgElement = document.querySelector('#qrCodeContainer svg');
                    const svgData = new XMLSerializer().serializeToString(svgElement);
                    
                    // SVG 데이터를 Base64로 인코딩
                    const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
                    
                    // 이미지 생성 및 로드
                    const img = new Image();
                    img.onload = function() {
                        // Canvas 생성 및 이미지 그리기
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        
                        // Canvas에서 Blob 생성
                        canvas.toBlob(blob => {
                        // 새로운 ClipboardItem 생성
                        const clipboardItem = new ClipboardItem({ 'image/png': blob });
                        
                        // 클립보드에 복사
                        navigator.clipboard.write([clipboardItem])
                            .then(() => {
                            alert(language === 'KR' 
                                ? 'QR 코드가 클립보드에 복사되었습니다!' 
                                : 'QR code copied to clipboard!');
                            })
                            .catch(err => {
                            console.error('클립보드 복사 오류:', err);
                            alert(language === 'KR' 
                                ? '클립보드에 복사할 수 없습니다. 브라우저가 이 기능을 지원하지 않을 수 있습니다.' 
                                : 'Cannot copy to clipboard. Your browser may not support this feature.');
                            });
                        });
                    };
                    img.src = 'data:image/svg+xml;base64,' + svgBase64;
                    } catch (error) {
                    console.error('QR 코드 복사 오류:', error);
                    alert(language === 'KR' 
                        ? 'QR 코드 복사 중 오류가 발생했습니다. 브라우저가 이 기능을 지원하지 않을 수 있습니다.' 
                        : 'Error copying QR code. Your browser may not support this feature.');
                    }
                }}
                className="qr-btn"
                >
                {language === 'KR' ? 'QR 코드 복사' : 'Copy QR Code'}
                </button>
                
                <button
                onClick={() => {
                    if (navigator.share) {
                    navigator.share({
                        title: language === 'KR' ? 'QR 코드 메시지' : 'QR Code Message',
                        text: language === 'KR' ? '새로운 QR 코드 메시지를 확인해보세요!' : 'Check out this QR code message!',
                        url: messageData.url
                    })
                    .catch(err => {
                        console.error('공유 실패:', err);
                    });
                    } else {
                    alert(language === 'KR' ? '이 브라우저는 공유 기능을 지원하지 않습니다.' : 'Sharing is not supported in this browser.');
                    }
                }}
                className="qr-btn"
                >
                {language === 'KR' ? '공유하기' : 'Share'}
                </button>
            </div>
            </div>
            
            <button
            onClick={() => setActiveView('create')}
            className="qr-btn qr-btn-secondary"
            >
            {language === 'KR' ? '새 메시지 만들기' : 'Create New Message'}
            </button>
        </div>
        )}
        
        {/* 메시지 목록 화면 */}
        {activeView === 'list' && (
          <QRMessageList 
            language={language} 
            user={user} 
            SERVER_ROOT={SERVER_ROOT}
          />
        )}
        
        {/* 템플릿 관리 화면 */}
        {activeView === 'templates' && (
          <QRTemplateManager 
            language={language} 
            user={user} 
            SERVER_ROOT={SERVER_ROOT}
          />
        )}
        
        {/* QR 코드 뷰어 */}
        {activeView === 'view' && messageData && (
          <QRViewer 
            language={language} 
            message={messageData}
            user={user}
            onPasswordSubmit={handlePasswordSubmit}
            SERVER_ROOT={SERVER_ROOT}
          />
        )}
      </main>
      
      <footer className="qr-footer">
        <button onClick={goToHome} className="qr-btn qr-btn-back">
          {language === 'KR' ? '홈으로 돌아가기' : 'Back to Home'}
        </button>

        {/* <button 
            onClick={() => {
            try {
                window.close();
            } catch (e) {
                // 창 닫기가 실패한 경우 홈으로 이동
                window.location.href = '/';
            }
            }} 
            className="qr-btn qr-btn-back"
        >
            {language === 'KR' ? '창 닫기' : 'Close Window'}
        </button> */}
      </footer>
    </div>
  );
}

export default QRclient;